import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, X, Info, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BLOCK_SCHEMAS, safeImage } from "@/components/home/pageBlocks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export const ImageField = ({ value, onChange, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("image_type", "general");
      const r = await axios.post(`${API}/upload/image`, fd, { headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` } });
      onChange(r.data.image_url || r.data.url);
      toast.success("Bild hochgeladen");
    } catch {
      toast.error("Bild konnte nicht hochgeladen werden");
    } finally {
      setBusy(false);
    }
  };
  const src = safeImage(value, BACKEND_URL);
  return (
    <div className="space-y-2">
      {src && (
        <div className="relative">
          <img src={src} alt="" className="w-full h-28 object-cover border border-[#E5E5E5]" />
          <button type="button" onClick={() => onChange("")} className="absolute top-1 right-1 bg-black text-white p-1" aria-label="Bild entfernen" data-testid={`${testid}-clear`}>
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`${testid}-file`} />
      <div className="flex gap-2">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://... oder hochladen" data-testid={testid} />
        <button type="button" onClick={() => ref.current?.click()} disabled={busy}
          className="px-3 bg-black text-white text-xs font-bold flex items-center gap-1 hover:bg-[#333] disabled:opacity-50" data-testid={`${testid}-upload`}>
          <Upload className="w-3 h-3" /> {busy ? "..." : "Hochladen"}
        </button>
      </div>
    </div>
  );
};

export const BlockProps = ({ block, onChange }) => {
  if (!block) {
    return <p className="text-sm text-[#888B8D]" data-testid="editor-no-selection">Wählen Sie links einen Bereich oder klicken Sie in der Vorschau auf „bearbeiten“.</p>;
  }
  const schema = BLOCK_SCHEMAS[block.type] || { fields: [] };
  const props = block.props || {};
  const set = (key, val) => onChange({ ...block, props: { ...props, [key]: val } });
  const reset = () => onChange({ ...block, props: {} });

  return (
    <div className="space-y-4" data-testid="editor-props">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-black">{schema.label}</h3>
        {schema.builtin && Object.keys(props).length > 0 && (
          <button type="button" onClick={reset} className="text-xs text-[#888B8D] hover:text-black flex items-center gap-1" data-testid="editor-reset-block">
            <RotateCcw className="w-3 h-3" /> Original-Texte
          </button>
        )}
      </div>
      {schema.info && (
        <p className="text-xs bg-[#F2F2F2] p-3 flex gap-2 text-[#555]"><Info className="w-4 h-4 flex-shrink-0" />{schema.info}</p>
      )}
      {schema.fields.map((f) => {
        const value = props[f.key] ?? (schema.builtin ? "" : f.def);
        const id = `editor-field-${f.key}`;
        return (
          <div key={f.key} className="space-y-1.5">
            <Label htmlFor={id} className="text-xs font-bold uppercase tracking-wide text-[#555]">{f.label}</Label>
            {f.kind === "textarea" && (
              <Textarea id={id} rows={4} value={props[f.key] ?? ""} placeholder={schema.builtin ? f.def : ""}
                onChange={(e) => set(f.key, e.target.value)} data-testid={id} />
            )}
            {f.kind === "text" && (
              <Input id={id} value={props[f.key] ?? ""} placeholder={schema.builtin ? f.def : ""}
                onChange={(e) => set(f.key, e.target.value)} data-testid={id} />
            )}
            {f.kind === "image" && <ImageField value={props[f.key]} onChange={(v) => set(f.key, v)} testid={id} />}
            {f.kind === "select" && (
              <div className="flex gap-1 flex-wrap" data-testid={id}>
                {f.options.map(([v, l]) => (
                  <button key={v} type="button" onClick={() => set(f.key, v)}
                    className={`px-3 py-1.5 text-xs font-bold border ${value === v ? "bg-black text-[#EFDF00] border-black" : "bg-white border-[#D9D9D6] hover:border-black"}`}
                    data-testid={`${id}-${v}`}>{l}</button>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {schema.builtin && schema.fields.length > 0 && (
        <p className="text-xs text-[#888B8D]">Leere Felder zeigen den Original-Text (grau vorgeschlagen).</p>
      )}
    </div>
  );
};
